import { BLEED_TINT_FILL } from '../constants/bleed-tint-fill'
import { OUTER_YELLOW_BLEED_COPY } from '../constants/bleed-copy'
import { computePrintBleedTintBands } from '../helpers/print-bleed-tint-bands'
import type { BleedMetrics } from '../types/bleed-metrics'

type PrintBleedTintsProps = BleedMetrics & {
  tintZIndex?: number
  showHint?: boolean
  /**
   * When &gt; 0 (double spread), bindery gutter: the top/bottom in-trim rows here are split in *x*
   * so they leave the gutter's own column untouched, rather than painting under it. The gutter
   * column's own fill is `DoubleSpreadGutterTintAboveShapes`
   * (`center-canvas/components/screen-space-layers.tsx`), not drawn here — see that component for
   * why. `SpineGutterOverlay` in the document has no fill either (bridges/ labels only).
   */
  centerGap?: number
  /**
   * Gates every yellow band drawn here — both the in-trim safety band (between the dashed trim
   * and the live page) and the outer band (between the dashed trim and the true bleed edge, plus
   * the pasteboard-corner fillers that complete its shape) — fed from the editor's "Show Bleed
   * Bar" toggle. Defaults to `true` so non-editor callers (stories) keep today's always-on look.
   */
  showOutsideTrim?: boolean
}

/**
 * Print-bleed tint ring: `bleedMargin` pt outside the dashed trim + `PRINT_BLEED_TINT_INSIDE_TRIM_PT`
 * toward the page (36 pt per edge). All bands use `--doc-bleed-tint-outside-trim` for one consistent yellow.
 */
export function PrintBleedTints({
  bleedWidth,
  bleedHeight,
  bleedMargin,
  trimWidth,
  trimHeight,
  tintZIndex = 3,
  showHint = true,
  centerGap = 0,
  showOutsideTrim = true,
}: PrintBleedTintsProps) {
  const bands = computePrintBleedTintBands({
    bleedWidth,
    bleedHeight,
    bleedMargin,
    trimWidth,
    trimHeight,
    centerGap,
    showOutsideTrim,
  })

  if (!bands) {
    return null
  }

  return (
    <>
      {bands.map((band) => (
        <div
          key={band.key}
          className={
            band.anchoredTopLeft
              ? 'doc-bleed-tint pointer-events-none absolute left-0 top-0'
              : 'doc-bleed-tint pointer-events-none absolute'
          }
          style={{ zIndex: tintZIndex, ...band.rect, ...BLEED_TINT_FILL }}
        />
      ))}
      {showHint && showOutsideTrim && (
        <div
          className="doc-band__label pointer-events-none absolute text-center whitespace-normal text-[8px] leading-[10px] text-[color:var(--doc-label)] select-none"
          style={{
            left: bleedWidth / 2,
            top: bleedMargin / 2,
            transform: 'translate(-50%, -50%)',
            maxWidth: Math.max(48, bleedWidth - 2 * bleedMargin - 16),
            zIndex: tintZIndex + 10,
          }}
          aria-hidden
        >
          {OUTER_YELLOW_BLEED_COPY}
        </div>
      )}
    </>
  )
}
